/**
 * Central resolver for tenant API base URLs (STEP 3: region-key + env map).
 *
 * SINGLE SOURCE OF TRUTH: the app persists only the primary region key
 * (`CF_PRIMARY_TENANT` = 'default' | 'kenya'). Every URL is derived from that
 * key through TENANT_ENDPOINTS below, which is baked in at BUILD time from env.
 * Users cannot edit the bundle, so a tampered key can only ever select another
 * VALID region (or fall back to default) — it can never point the app at an
 * attacker's host.
 *
 * Every export is a FUNCTION, resolved fresh each call — never cache in a
 * module-level const (that reintroduces the stale-URL bug).
 *
 * Access from services / plain modules: import { adminBaseUrl, ... }.
 * Access from components: same import, or this-free where possible.
 */

// Build-time allowlist. process.env.VUE_APP_* are inlined as string literals by
// webpack, so this map is a fixed set of hosts. Add a region here + its env vars.
const TENANT_ENDPOINTS = {
    default: {
      admin: process.env.VUE_APP_BASE_URL,
      defo: process.env.VUE_APP_DEFORESTATION_BASE_URL,
      defoApi: process.env.VUE_APP_DEFORESTRATION_API_BASE_URL,
      reporting: process.env.VUE_APP_DEFORESTRATION_REPORTING_API_BASE_URL,
    },
    kenya: {
      admin: process.env.VUE_APP_KENYA_BASE_URL,
      defo: process.env.VUE_APP_KENYA_DEFO_BASE_URL,
      defoApi: process.env.VUE_APP_KENYA_DEFO_API_BASE_URL,
      reporting: process.env.VUE_APP_KENYA_REPORTING_API_BASE_URL,
    },
  };
  
  const DEFAULT_KEY = 'default';
  const PRIMARY_TENANT_KEY = 'CF_PRIMARY_TENANT';
  
  function ls(key) {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key) || null;
  }
  
  // Trim, strip wrapping quotes, and drop trailing slashes — some env values on
  // live are quoted / space-padded (e.g. `KEY= 'https://...'`).
  function normalize(url) {
    if (!url) return '';
    return String(url).trim().replace(/^['"]+|['"]+$/g, '').replace(/\/+$/, '');
  }
  
  function endpointsFor(key) {
    return TENANT_ENDPOINTS[key] || TENANT_ENDPOINTS[DEFAULT_KEY];
  }
  
  /** Set of valid region keys (the allowlist). */
  export function tenantKeys() {
    return Object.keys(TENANT_ENDPOINTS);
  }
  
  /**
   * The primary region the user logged into. Read from the persisted key and
   * validated against the allowlist. Falls back to deriving from the legacy
   * `CF_BASE_URL` (pre-step-3 sessions), then to 'default'.
   */
  export function primaryTenantKey() {
    const stored = ls(PRIMARY_TENANT_KEY);
    if (stored && TENANT_ENDPOINTS[stored]) return stored;
  
    // Legacy fallback: older sessions only saved the raw admin URL.
    const legacy = normalize(ls('CF_BASE_URL'));
    if (legacy) {
      const kenyaAdmin = normalize(TENANT_ENDPOINTS.kenya.admin);
      if (kenyaAdmin && legacy === kenyaAdmin) return 'kenya';
    }
    return DEFAULT_KEY;
  }
  
  /** Admin base URL for a specific region key (used by tenant-session infra). */
  export function adminUrlForKey(key) {
    return normalize(endpointsFor(key).admin);
  }
  
  /**
   * One-time upgrade for sessions created before the key was stored: if only the
   * legacy CF_BASE_URL is present, derive and persist CF_PRIMARY_TENANT so
   * already logged-in users transition WITHOUT re-login. Safe to call every boot.
   */
  export function migrateLegacyTenantKey() {
    if (typeof localStorage === 'undefined') return;
    if (localStorage.getItem(PRIMARY_TENANT_KEY)) return;
    const legacy = normalize(localStorage.getItem('CF_BASE_URL'));
    if (!legacy) return;
    const kenyaAdmin = normalize(TENANT_ENDPOINTS.kenya.admin);
    localStorage.setItem(PRIMARY_TENANT_KEY, kenyaAdmin && legacy === kenyaAdmin ? 'kenya' : DEFAULT_KEY);
  }
  
  /** Main admin API base for the primary tenant, e.g. https://host/api/admin */
  export function adminBaseUrl() {
    return normalize(endpointsFor(primaryTenantKey()).admin);
  }
  
  /** Admin API host without the trailing /admin, e.g. https://host/api */
  export function apiRoot() {
    return adminBaseUrl().replace('/admin', '');
  }
  
  /** Deforestation GraphQL base for the primary tenant */
  export function deforestationBaseUrl() {
    return normalize(endpointsFor(primaryTenantKey()).defo);
  }
  
  /** Deforestation REST API base for the primary tenant */
  export function deforestationApiBaseUrl() {
    return normalize(endpointsFor(primaryTenantKey()).defoApi);
  }
  
  /** Reporting API base for the primary tenant */
  export function reportingBaseUrl() {
    return normalize(endpointsFor(primaryTenantKey()).reporting);
  }